/**
 * Vorlage „Reiben“.
 *
 * Eine Vierkantreibe steht auf dem Brett. Die Zutat wird mit leichtem Druck
 * auf und ab über die Reibfläche geführt, wird dabei kürzer, und darunter
 * wächst ein Haufen feiner Raspel.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { circle, compact, faded, group, line, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { falling } from '../parts/effects.ts';
import { counter, doubleArrow, GROUND_Y, shadow } from '../parts/stage.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Reibe in der Vorderansicht. */
const GRATER = { cx: 104, top: 26, topWidth: 44, bottomWidth: 60 } as const;

/** Zeitplan. */
const TIME = { grate: [0.04, 0.84], reset: [0.9, 1] } as const;
/** Auf- und Abbewegungen je Schleife. */
const STROKES = 6;

/** Zutaten zum Reiben. */
const ITEMS = { cheese: 'Käse', carrot: 'Möhre', potato: 'Kartoffel' } as const;
type Item = keyof typeof ITEMS;
const TONES: Readonly<Record<Item, Tone>> = { cheese: 'shade', carrot: 'shade', potato: 'paper' };

/** Reibe mit Griff und Reiblöchern. */
function grater(): SceneNode[] {
  const { cx, top, topWidth, bottomWidth } = GRATER;
  const body = path()
    .move(cx - topWidth / 2, top)
    .line(cx + topWidth / 2, top)
    .line(cx + bottomWidth / 2, GROUND_Y)
    .line(cx - bottomWidth / 2, GROUND_Y)
    .close()
    .toString();
  const holes: SceneNode[] = [];
  for (let row = 0; row < 9; row += 1) {
    const y = top + 14 + row * 11;
    const half = lerp(topWidth, bottomWidth, (y - top) / (GROUND_Y - top)) / 2 - 8;
    for (let column = 0; column < 4; column += 1) {
      const x = cx - half + ((half * 2) / 3) * column + (row % 2) * 4 - 2;
      holes.push(
        shape(
          path()
            .move(x - 3, y + 1)
            .quad(x, y - 3, x + 3, y + 1)
            .toString(),
          { stroke: 'ink', strokeWidth: STROKE.fine },
        ),
        circle(x, y + 2, 1.2, styles.flat('cavity')),
      );
    }
  }
  return [
    shape(roundedRectPath(cx - 14, top - 16, 28, 20, 8), { stroke: 'ink', strokeWidth: STROKE.outline }),
    shape(body, styles.solid('paper')),
    ...holes,
    line(cx - topWidth / 2 + 2, top + 5, cx + topWidth / 2 - 2, top + 5, styles.fine('shade')),
  ];
}

/** Die Zutat als Block, der gegen die Reibfläche gedrückt wird; length schrumpft. */
function block(item: Item, x: number, y: number, length: number): SceneNode {
  const tone = TONES[item];
  if (item === 'carrot') {
    const d = path()
      .move(x, y - 10)
      .line(x - length, y - 6)
      .quad(x - length - 6, y, x - length, y + 6)
      .line(x, y + 10)
      .close()
      .toString();
    return group([
      shape(d, { fill: tone, stroke: 'ink', strokeWidth: STROKE.outline }),
      line(x - length * 0.6, y - 5, x - length * 0.5, y - 2, styles.fine('deep')),
    ]);
  }
  const d = roundedRectPath(x - length, y - 13, length, 26, 3);
  const dots: SceneNode[] =
    item === 'cheese'
      ? [
          circle(x - length * 0.3, y - 4, 3, styles.flat('tint')),
          circle(x - length * 0.65, y + 5, 2.4, styles.flat('tint')),
        ]
      : [];
  return group([
    shape(d, { fill: tone, stroke: 'ink', strokeWidth: STROKE.outline }),
    group(dots, { clip: d }),
  ]);
}

/** Haufen der Raspel vor der Reibe; amount 0 bis 1. */
function heap(item: Item, amount: number): SceneNode | null {
  if (amount <= 0.01) {
    return null;
  }
  const height = 4 + amount * 16;
  const width = 30 + amount * 36;
  const cx = GRATER.cx;
  const d = path()
    .move(cx - width, GROUND_Y + 1)
    .curve(cx - width * 0.6, GROUND_Y - height, cx + width * 0.6, GROUND_Y - height, cx + width, GROUND_Y + 1)
    .close()
    .toString();
  const strands: SceneNode[] = [-0.6, -0.3, 0, 0.3, 0.6].map((f, index) =>
    shape(
      path()
        .move(cx + width * f - 6, GROUND_Y - 2 - (index % 2) * 3)
        .quad(cx + width * f, GROUND_Y - height * 0.7, cx + width * f + 6, GROUND_Y - 3)
        .toString(),
      styles.fine('deep'),
    ),
  );
  return group([
    shape(d, { fill: TONES[item], stroke: 'ink', strokeWidth: STROKE.detail }),
    group(strands, { clip: d }),
  ]);
}

/** Vorlage „Reiben“. */
export const cutGrateTemplate = defineTemplate({
  id: 'cut.grate',
  group: 'cut',
  title: 'Reiben',
  description:
    'Die Zutat wird mit leichtem Druck auf und ab über die Reibe geführt. Darunter sammeln sich die feinen Raspel.',
  durationSeconds: 5,
  keywords: ['reiben', 'gerieben', 'raspeln', 'geraspelt', 'reibe', 'hobeln'],
  stillTime: 0.5,
  parameters: [choice('item', 'Zutat', ITEMS, 'cheese')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/käse|parmesan|gouda|emmentaler/.test(lower)) return { item: 'cheese' };
    if (/möhre|karotte/.test(lower)) return { item: 'carrot' };
    if (/kartoffel/.test(lower)) return { item: 'potato' };
    return {};
  },
  scene(values, t) {
    const item = textValue(values, 'item', 'cheese') as Item;
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const grate = segment(t, ...TIME.grate);
    const active = grate > 0 && grate < 1;
    const amount = ease.outQuad(grate);
    // Auf und ab entlang der Reibfläche; die Zutat liegt links an der Reibe an.
    const stroke = active ? (1 - Math.cos(grate * STROKES * Math.PI * 2)) / 2 : 0;
    const y = lerp(54, 112, stroke);
    const faceX =
      GRATER.cx -
      lerp(GRATER.topWidth, GRATER.bottomWidth, (y - GRATER.top) / (GROUND_Y - GRATER.top)) / 2 +
      26;
    const length = lerp(62, 26, amount) * (1 - reset) + 62 * reset;
    const scraps = active
      ? falling(faceX - 6, y + 10, GROUND_Y - 6, 14, 6, t, {
          seed: 6,
          size: 1.3,
          loops: 6,
          tone: TONES[item] === 'paper' ? 'deep' : TONES[item],
        })
      : [];
    return compact([
      ...counter(),
      shadow(GRATER.cx, GRATER.bottomWidth * 0.7),
      ...grater(),
      amount > 0.01 ? faded(1 - reset, [heap(item, amount)]) : null,
      ...scraps,
      block(item, faceX, y, length),
    ]);
  },
  hints() {
    return [doubleArrow({ x: 40, y: 50 }, { x: 40, y: 116 })];
  },
});
